import { useFormContext } from 'react-hook-form';

import clsx from 'clsx';

//hooks
import useAcl from '@/hooks/user/useAcl';
import useTranslation from '@/utils/hooks/translation/useTranslation';

//type
import type { PlatformFormType } from '@/components/pages/settings/platform/types';

//libs
import { aclFeatures } from '@/lib/acl/aclFeatures';
import { companyLogoImageConfig } from '@/lib/files/images';

import FileUploader from '@/components/base/FileUploader';
import FormFieldMessage from '@/components/base/FormFieldMessage';
//components
import Icon from '@/components/base/Icon';
import Tooltip from '@/components/base/Tooltip';

type PlatformCompanyLogoProps = Readonly<{
    handleOnFileChange: (file: File | string) => void;
    hasPreview: boolean;
    companyLogo?: string;
}>;

export function PlatformCompanyLogo({
    handleOnFileChange,
    hasPreview,
    companyLogo
}: PlatformCompanyLogoProps) {
    const { t } = useTranslation('settings');
    const { hasAcl } = useAcl();

    const {
        register,
        setValue,
        formState: { errors }
    } = useFormContext<PlatformFormType>();

    if (!hasAcl(aclFeatures.report.white_label.update)) {
        return null;
    }

    return (
        <div className="w-full">
            <div className="flex items-center">
                <p className="mb-2 font-GSemiBold text-xs text-icoGray-500 dark:text-icoDarkMode-wolf">
                    {t('settings:platform_page.company_logo')}
                </p>
                <Tooltip placement="top" content={t('settings:platform_page.company_logo_tooltip')}>
                    <Icon icon="info" className="mb-2 ml-1 text-xs text-icoGray-400" />
                </Tooltip>
            </div>
            <FileUploader
                {...register('fileInput')}
                handleOnFileChange={handleOnFileChange}
                minWidth={companyLogoImageConfig.minWidth}
                minHeight={companyLogoImageConfig.minHeight}
                maxSize={companyLogoImageConfig.maxSize}
                allowedExtensions={companyLogoImageConfig.allowedExtensions}>
                <FileUploader.ErrorBanner className="mb-5" />
                <div className="flex w-full items-center">
                    {hasPreview ? (
                        <div className="mr-2 w-1/2">
                            <FileUploader.ImagePreview
                                className="!mr-0 h-12 border border-icoGray-300 dark:border-icoDarkMode-beton"
                                url={companyLogo}>
                                <FileUploader.RemoveButton
                                    setValue={setValue} // Mandatory reset function from react-hook-form
                                    inputName="fileInput"
                                    as="button"
                                    handleOnFileChange={handleOnFileChange}
                                    className="absolute -right-2 -top-2 z-1 flex h-4 w-4 min-w-min items-center justify-center rounded-full bg-icoRed p-0">
                                    <Icon icon="close" className="text-xs text-white" />
                                </FileUploader.RemoveButton>
                            </FileUploader.ImagePreview>
                        </div>
                    ) : null}

                    <FileUploader.ButtonUpload
                        icon="upload"
                        className={clsx('h-12 min-w-0', {
                            'ml-2 w-1/2': hasPreview,
                            'w-full': !hasPreview
                        })}>
                        {hasPreview
                            ? t('platform_page.update_company_logo')
                            : t('platform_page.upload_company_logo')}
                    </FileUploader.ButtonUpload>
                </div>
            </FileUploader>
            {errors.fileInput && typeof errors.fileInput.message === 'string' ? (
                <FormFieldMessage>{errors.fileInput.message}</FormFieldMessage>
            ) : null}
        </div>
    );
}
